import React, { useEffect, useMemo, useState } from 'react';
import { useSellerHubCart } from '../hooks/use-seller-hub-cart';
import {
  getExtraDetails,
  getCatalogConfig,
  getWorkspaceId,
  useAppSelector,
  OrderDataLine,
  OrderDataSummary,
  ProductCardVariant,
  dispatch,
  updateExtraDetails,
  updateSearchKey,
  UOM,
  removeOrderPricelist,
  PoOrderShipping,
  OrderLine,
} from '@zonofi/zono-money-store';
import { TouchableOpacity } from '../../common';
import { isEmpty } from 'lodash';
import {
  CommonButton,
  EmptyStateView,
  PullRefreshContainer,
} from '../../components';
import { SVGS, PNGS } from '../../assets';
import { OrderDetailsModalGroup, OrderLineCard } from '../../order';
import {
  OrderSummary,
  OrderSummaryCard,
} from '../cart-card/order-summary-card';

import {
  OrderDiscountLevel,
  OrderProductUpdateType,
  SELLER_WEBAPP_ROUTES,
} from '../../constants';
import { getFormattedAmount } from '@zonofi/common/functions/format-amount';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ReactComponent as ArrowDown } from '@zonofi/svg-icons/arrow_down.svg';
import { ReactComponent as CartProductIcon } from '@zonofi/svg-icons/cart-product.svg';
import { ReactComponent as RemoveIcon } from '@zonofi/svg-icons/remove.svg';
import { ReactComponent as InfoIcon } from '@zonofi/svg-icons/input-info.svg';
import './cart-details-content.scss';
import { CartOtherDetails } from './other-details/cart-other-details';
import { ReactComponent as ArrowRight } from '@zonofi/svg-icons/arrow_right.svg';
import {
  isOrderLineOutOfStock,
  orderHasOutOfStockLine,
} from '../../helper/is-order-line-out-of-stock';
import { PromotionsModal } from '../promotions-modal/promotions-modal';
import { AdvancePaymentActionCard } from './other-details/advance-payment-action-card';
import { useAdvancePaymentActions } from '../hooks/use-advance-payment-action';
interface CartDetailsContentProps {
  selectedTab: number;
  customerId: string;
  setSelectedTab?: React.Dispatch<React.SetStateAction<number>>;
}

export const CartDetailsContent: React.FC<CartDetailsContentProps> = ({
  selectedTab = 0,
  customerId = '',
  setSelectedTab,
}) => {
  const navigate = useNavigate();
  const workspaceId = useAppSelector(getWorkspaceId);
  const customerDetails = useAppSelector(getExtraDetails);
  const { showOutOfStockTag } = useAppSelector(getCatalogConfig);

  const [refreshNonce, setRefreshNonce] = useState(0);
  const [orderSummary, setOrderSummary] = useState<OrderSummary>();
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [lineDiscountData, setLineDiscountData] = useState<OrderDataLine>();
  const [orderLineDiscountData, setOrderLineDiscountData] =
    useState<OrderDataSummary>();
  const [orderDiscountType, setOrderDiscountType] =
    useState<OrderDiscountLevel>();
  const [orderDetailsInfo, setOrderDetailsInfo] = useState<{
    inputValues: Record<string, string>;
    includePaymentLink: boolean;
  }>();
  const [openReplaceProductDrawer, setOpenReplaceProductDrawer] = useState<{
    open: boolean;
    line?: OrderLine;
  }>({
    open: false,
    line: undefined,
  });
  const [openConfirmModal, setOpenConfirmModal] = useState<{
    open: boolean;
    heading: string;
    description: string;
  }>({
    open: false,
    heading: '',
    description: '',
  });
  const [openCheckoutSuccess, setOpenCheckoutSuccess] = useState<{
    open: boolean;
    message: string;
    description: string;
    orderId?: string;
  }>({
    open: false,
    message: '',
    description: '',
    orderId: '',
  });
  const [uomModalState, setUomModalState] = useState<{
    open: boolean;
    line?: OrderLine;
  }>({
    open: false,
    line: undefined,
  });
  const [openPricelistDrawer, setOpenPriceListDrawer] =
    useState<boolean>(false);

  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);

  const refresh = () => {
    setRefreshNonce((prev) => prev + 1);
  };

  const {
    callCartAPI,
    cartAPIResponse,
    productsInCart,
    handleUpdate,
    cartSummaryAPIResponse: cartSummaryRes,
    onRemovePress,
    onLineDiscountRemove,
    onApplyDiscount,
    orderDiscount,
    onCartCheckout,
    checkoutApiResponse,
    onReplaceProduct,
    poFileId,
    confirmationLoading,
    onAskConfirmation,
    selectedPricelist,
    onSelectPricelist,
    onSaveDetails,
    savingDetailsLoader,
    selectedBenefit,
    showPromotionsModal,
    applyPromotionOnOrder,
    promotionSchemes,
    onSkipPromotion,
    onCancelPromotion,
    openPromotionsModal,
    removeId,
    handleRemove,
    advanceRemoveLoading,
  } = useSellerHubCart({
    sellerWorkspaceId: workspaceId,
    customerId: customerId,
  });

  const { manageAdvanceActions } = useAdvancePaymentActions({
    setSelectedTab,
    handleRemove,
    removeLoading: advanceRemoveLoading,
  });

  const [selectedLocation, setSelectedLocation] = useState<PoOrderShipping>({
    ...(cartAPIResponse?.data?.shippingAddress as PoOrderShipping),
  });

  const hasOutOfStockItem = useMemo(
    () => orderHasOutOfStockLine(productsInCart, !!showOutOfStockTag),
    [productsInCart, showOutOfStockTag]
  );

  const hasAvailablePromotions = promotionSchemes.length > 0;

  /* ---------- existing effects ---------- */
  useEffect(() => {
    if (!workspaceId || !customerId) return;
    callCartAPI();
  }, [workspaceId, customerId, refreshNonce]);

  useEffect(() => {
    if (cartSummaryRes.isSuccess && cartSummaryRes.data) {
      const summary = cartSummaryRes.data;
      setOrderSummary({
        skuCount: summary?.totalSKUCount,
        quantity: summary?.totalItemsCount,
        orderAmount: summary?.totalOrderValue,
        schemeSaving: summary?.totalDiscount,
        grossAmount: summary?.grossOrderNumber,
        tax: summary?.totalTax,
        orderValue: summary?.grossOrderValue,
      });
    }
  }, [cartSummaryRes]);

  const onReplacePress = (lineItem: OrderLine, type: ProductCardVariant) => {
    dispatch(
      updateExtraDetails({
        ...customerDetails,
        productVariantId: lineItem?.productVariantId,
        variantType: type,
        poFileId: lineItem?.poFileId,
        poFileLineId: lineItem?.poFileLineId,
        orderLineId: lineItem?.id,
        orderId: lineItem?.orderId?.toString(),
      })
    );
    setOpenReplaceProductDrawer({ open: true, line: lineItem });
  };

  const openUomModal = (line: OrderLine) => {
    setUomModalState({ open: true, line });
  };

  const closeUomModal = () => {
    setUomModalState({ open: false, line: undefined });
  };

  const onCloseReplace = () => {
    callCartAPI();
    dispatch(updateSearchKey(''));
    setOpenReplaceProductDrawer({ open: false, line: undefined });
  };

  const onAddOrderLevelDiscount = () => {
    if (orderSummary)
      setOrderLineDiscountData({
        total: orderSummary.grossAmount,
        discount: orderSummary.schemeSaving,
        totalWithTax: orderSummary.orderValue,
        tax: orderSummary.tax,
        shippingLines: cartAPIResponse?.data?.shippingLines ?? [],
        lines: productsInCart.map((lineItem) => ({
          productVariantId: Number(lineItem?.productVariantId),
          price: Number(lineItem?.unitPrice),
          newQty: Number(lineItem?.quantity),
          quantity: Number(lineItem?.quantity),
          title: lineItem.productVariant?.translations?.[0]?.name,
          orderId: lineItem?.orderId,
        })),
      });

    setOrderDiscountType('order');
    setShowOrderSummary(false);
    setShowDiscountModal(true);
  };

  const onChangeUom = (uom: UOM) => {
    const lineItem = uomModalState.line;
    if (lineItem) {
      handleUpdate(
        {
          ...lineItem,
          newQnty: Number(lineItem.quantity) * uom.packSize,
        },
        OrderProductUpdateType.QUANTITY
      );
    }
    closeUomModal();
  };

  useEffect(() => {
    if (checkoutApiResponse.isSuccess) {
      const orders = checkoutApiResponse.data.orders;
      setOpenConfirmModal({
        open: false,
        heading: '',
        description: '',
      });
      setOpenCheckoutSuccess({
        open: true,
        message: 'Checkout Successful',
        description: `Order with ${orderSummary?.skuCount} SKU’s has been placed!\nOrder Number: ${orders?.map((x) => x?.refOrderNumber).join(', ')}`,
        orderId: orders[0]?.orderId.toString(),
      });
    }
  }, [checkoutApiResponse]);

  const fetchLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setSelectedLocation((prev) => ({ ...prev, latitude, longitude }));
      },
      (err) => {
        console.log('Location Error: ', err);
      },
      { enableHighAccuracy: false }
    );
  };

  const advancePaymentCardProps = useMemo(() => {
    // If advancePayment exists on order
    if (Number(cartAPIResponse?.data?.advancePayment)) {
      return {
        advancePayment: Number(cartAPIResponse?.data?.advancePayment) || 0,
        advancePaymentType: cartAPIResponse?.data?.advancePaymentType as
          | 'amount'
          | 'percentage',
        advancePaymentAmount:
          Number(cartAPIResponse?.data?.advancePaymentAmount) || 0,
      };
    }

    return null;
  }, [cartAPIResponse?.data]);

  return (
    <>
      {selectedTab === 0 &&
        (isEmpty(productsInCart) ? (
          <EmptyStateView
            imageSrc={SVGS.NoCartItems}
            message="Your cart is empty"
            description="Start adding products to place your order"
          />
        ) : (
          <div
            className="cart-products-view"
            style={{
              paddingBottom: advancePaymentCardProps ? '120px' : '70px',
            }}
          >
            <div className="cart-products-view--count-pricelist">
              <div className="cart-products-view--count-view">
                <CartProductIcon />
                <span className="cart-products-view--count-view-text font-semibold">
                  {`${productsInCart.length} items`}
                </span>
              </div>

              <div className="cart-products-view--pricelist-content">
                <TouchableOpacity
                  className="cart-products-view--pricelist-view"
                  onPress={() => setOpenPriceListDrawer(true)}
                >
                  <span className="cart-products-view--pricelist-view-text font-regular">
                    {selectedPricelist?.pricelistName
                      ? selectedPricelist?.pricelistName
                      : 'Select Pricelist'}
                  </span>
                  <ArrowDown />
                </TouchableOpacity>
                {selectedPricelist?.pricelistName && (
                  <TouchableOpacity
                    onPress={() => dispatch(removeOrderPricelist())}
                  >
                    <RemoveIcon color="#dd360c" />
                  </TouchableOpacity>
                )}
              </div>
            </div>

            <PullRefreshContainer
              className="cart-products-view--wrapper"
              onRefresh={refresh}
            >
              {productsInCart.map((lineItem, index) => {
                return (
                  <OrderLineCard
                    key={`${lineItem.productVariantId}-${index}`}
                    lineItem={lineItem}
                    isOutOfStock={isOrderLineOutOfStock(
                      lineItem,
                      !!showOutOfStockTag
                    )}
                    onRemovePress={onRemovePress}
                    handleUpdate={handleUpdate}
                    onLineDiscountRemove={() => onLineDiscountRemove(lineItem)}
                    showRemoveLine={true}
                    onAddingLineDiscount={(data, discountType) => {
                      setLineDiscountData(data);
                      setShowDiscountModal(true);
                      setShowOrderSummary(false);
                      setOrderDiscountType(discountType);
                    }}
                    onReplacePress={onReplacePress}
                    openUomModal={openUomModal}
                    isCart={true}
                    isRemoving={removeId === lineItem?.id}
                  />
                );
              })}

              {hasAvailablePromotions && (
                <div className="order-details-footer-benefit-text font-semibold">
                  Benefits
                </div>
              )}

              {!selectedBenefit && hasAvailablePromotions && (
                <div
                  className="order-details-footer-apply-benefits font-semibold"
                  onClick={openPromotionsModal}
                >
                  <img src={PNGS.SchemePercentIcon} alt="" />
                  <span>Apply Benefits</span>
                  <ArrowRight color="#0067FF" />
                </div>
              )}

              {selectedBenefit && (
                <div className="order-details-selected-benefit">
                  <div className="order-details-selected-benefit-content">
                    <img src={PNGS.ManualOfferIcon} alt="benefit" />
                    <div className="order-details-selected-benefit-content-text">
                      <span className="font-semibold">
                        {selectedBenefit?.name}
                      </span>
                      {selectedBenefit.description ? (
                        <span className="font-regular">
                          {selectedBenefit?.description}
                        </span>
                      ) : null}
                      <span className="order-details-selected-benefit-meta font-regular">
                        {selectedBenefit?.code}
                        {selectedBenefit?.code &&
                          selectedBenefit?.minOrderQuantity &&
                          ` • `}
                        {selectedBenefit?.minOrderQuantity &&
                          `Min Order Quantity: ${selectedBenefit?.minOrderQuantity}`}
                      </span>
                      <button
                        className="order-details-selected-benefit-remove font-semibold"
                        onClick={openPromotionsModal}
                      >
                        Change benefit
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <OrderSummaryCard
                orderSummary={orderSummary as OrderSummary}
                title="Bill Summary"
              />
            </PullRefreshContainer>
          </div>
        ))}
      {selectedTab === 1 && (
        <>
          <CartOtherDetails
            onLocationClick={() => {
              fetchLocation();
              setShowLocationModal(true);
            }}
            location={selectedLocation}
            setOrderDetailsInfo={setOrderDetailsInfo}
            isEditable={true}
          />
          <div className="other-details-save-view">
            <CommonButton
              text="Save Details"
              onPress={() => {
                onSaveDetails?.(
                  orderDetailsInfo?.inputValues ?? {},
                  orderDetailsInfo?.includePaymentLink ?? false
                );
              }}
              buttonStyle="other-details-save-view-button"
              loading={savingDetailsLoader}
              spinnerColor={'white'}
              spinnerSize={20}
              disabled={false}
            />
          </div>
        </>
      )}

      {selectedTab === 0 && productsInCart.length > 0 && (
        <div className="cart-products-view--button-view-container">
          {advancePaymentCardProps && (
            <AdvancePaymentActionCard
              advancePayment={advancePaymentCardProps.advancePayment}
              advancePaymentType={advancePaymentCardProps.advancePaymentType}
              advancePaymentAmount={
                advancePaymentCardProps.advancePaymentAmount
              }
              actions={manageAdvanceActions}
            />
          )}
          {hasOutOfStockItem && (
            <div className="cart-products-view--oos-warning">
              <InfoIcon color="#fff" />
              <span className="font-semibold">
                Your cart contains an out-of-stock item. Please replace or
                remove it to continue.
              </span>
            </div>
          )}
          <div className="cart-details-bottom-container-view">
            {orderDiscount && (
              <>
                <span className="cart-details-bottom-container-discount font-medium">
                  <img src={SVGS.DiscountIcon} />
                  {`${getFormattedAmount(Number(orderDiscount))} Discount Applied`}
                </span>
                <div className="cart-details-bottom-container-border" />
              </>
            )}
            {!hasOutOfStockItem && (
              <TouchableOpacity onPress={onAddOrderLevelDiscount}>
                <span className="cart-details-bottom-container-text font-medium">
                  {orderDiscount ? 'Edit Discount' : '+ Add Discount'}
                </span>
              </TouchableOpacity>
            )}
          </div>
          <div className="cart-products-view--button-view">
            <CommonButton
              text="Ask Confirmation"
              onPress={onAskConfirmation}
              spinnerColor={'white'}
              spinnerSize={20}
              loading={confirmationLoading}
              buttonStyle={`confirmation-button-view ${
                hasOutOfStockItem ? 'disabled' : ''
              }`}
              disabled={hasOutOfStockItem}
            />
            <CommonButton
              text="Checkout"
              buttonStyle={`checkout-button-view ${
                hasOutOfStockItem ? 'disabled' : ''
              }`}
              disabled={hasOutOfStockItem}
              onPress={() =>
                setOpenConfirmModal({
                  open: true,
                  heading: 'Confirmation',
                  description: `Total ${productsInCart.length} products will be checked out`,
                })
              }
              endIcon={<ArrowRight color="#ffffff" />}
            />
          </div>
        </div>
      )}

      <OrderDetailsModalGroup
        discount={{
          open: showDiscountModal,
          onClose: () => setShowDiscountModal(false),
          onApply: onApplyDiscount,
          data: lineDiscountData as OrderDataLine,
          orderDiscountType: orderDiscountType as OrderDiscountLevel,
          orderSummary: orderLineDiscountData,
          orderLevelDiscount: undefined,
          isOrderSummary: showOrderSummary,
        }}
        replaceProduct={{
          open: openReplaceProductDrawer.open,
          onClose: onCloseReplace,
          lineItems: productsInCart.map((line) => ({
            productVariantId: Number(line?.productVariantId),
            quantity: line.quantity ?? 0,
            productMOQ: Number(line?.minOrderQty),
          })),
          onReplaceProduct: (pv) => {
            if (openReplaceProductDrawer.line)
              onReplaceProduct(pv, openReplaceProductDrawer.line);
          },
        }}
        pricelist={{
          open: openPricelistDrawer,
          onClose: () => setOpenPriceListDrawer(false),
          onSelectPricelist: onSelectPricelist,
        }}
        comfirm={{
          ...openConfirmModal,
          onClose: () =>
            setOpenConfirmModal({
              open: false,
              heading: '',
              description: '',
            }),
          onConfirmPress: onCartCheckout,
          isLoading: checkoutApiResponse?.isLoading,
        }}
        success={{
          ...openCheckoutSuccess,
          onClose: () => {
            setOpenCheckoutSuccess({
              open: false,
              message: '',
              description: '',
            });
          },
          showShareButton: false,
          mainText: 'View Order',
          onMainTextPress: () => {
            navigate(
              `${SELLER_WEBAPP_ROUTES.ORDER_DETAILS}/${poFileId}?orderId=${openCheckoutSuccess?.orderId}&customerId=${customerId}`
            );
          },
        }}
        uom={{
          open: uomModalState.open,
          onClose: closeUomModal,
          onUomChange: onChangeUom,
          currentLine: uomModalState.line,
        }}
        location={{
          open: showLocationModal,
          onClose: () => setShowLocationModal(false),
          posCords: {
            latitude: selectedLocation.latitude || 0,
            longitude: selectedLocation.longitude || 0,
          },
          onLocationSelected: (location) =>
            setSelectedLocation((prev) => ({ ...prev, streetLine1: location })),
        }}
      />
      {showPromotionsModal && (
        <PromotionsModal
          schemes={promotionSchemes}
          selectedBenefit={selectedBenefit}
          onApply={applyPromotionOnOrder}
          onSkip={() => {
            onSkipPromotion();
          }}
          onCancel={onCancelPromotion}
          isApplying={false}
          modalTitle={
            selectedBenefit ? 'Change Benefit' : 'Cash Discount Benefit'
          }
        />
      )}
    </>
  );
};
